/* facets-toolbar.js
 * Custom element <facets-toolbar> — filtrage DOM-based uniquement.
 * Scanne les cartes [data-product-block], construit dropdowns et filtre sans AJAX.
 */
(() => {
  'use strict';

  if (customElements.get('facets-toolbar')) return;

  customElements.define('facets-toolbar', class extends HTMLElement {

    connectedCallback() {
      this._container = this.closest('[data-section-id]');
      if (!this._container) return;

      this._activeFilters = { color: new Set(), available: new Set(), price: new Set(), sizes: new Set() };
      this._priceBuckets  = {};
      this._dropItems     = [];
      this._openKey       = null;

      this._sortToggle       = this.querySelector('[data-sort-toggle]');
      this._sortOptions      = this.querySelector('[data-collection-sort-options]');
      this._priceSortToggle  = this.querySelector('[data-price-sort-toggle]');
      this._priceSortOptions = this.querySelector('[data-price-sort-options]');

      this._onBodyClick = this._onBodyClick.bind(this);
      this._onKeyUp     = this._onKeyUp.bind(this);

      if (this._sortToggle) {
        this._sortToggle.addEventListener('click', () => this._sortDropdownToggle());
      }
      if (this._priceSortToggle) {
        this._priceSortToggle.addEventListener('click', () => this._priceSortDropdownToggle());
      }
      if (this._sortOptions) {
        this._sortOptions.querySelectorAll('[data-input-sort]').forEach(input => {
          input.addEventListener('change', e => this._onSortChange(e));
        });
      }

      this._initFiltersFromUrl();
      this._buildDropdowns();

      const hasRestoredFilters = Object.values(this._activeFilters).some(s => s.size > 0);
      if (hasRestoredFilters) {
        this._applyFilters();
        this._dropItems.forEach(({ btn, key }) => this._updateBtnLabel(btn, key));
        this._updatePriceBtnLabel();
        this._syncClearBtn();
      } else {
        this._updateCount();
      }

      document.addEventListener('click', this._onBodyClick);
      document.addEventListener('keyup', this._onKeyUp);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onBodyClick);
      document.removeEventListener('keyup', this._onKeyUp);
    }

    /* ── Cards ───────────────────────────────────────────────────────── */

    _allCards() {
      return [...(this._container.querySelectorAll('[data-product-block]') ?? [])];
    }

    _scanOptions() {
      const opts = { color: new Set(), available: new Set(), sizes: new Set(), prices: [] };
      this._allCards().forEach(card => {
        if (card.dataset.colorLabel) opts.color.add(card.dataset.colorLabel);
        if (card.dataset.available)  opts.available.add(card.dataset.available);
        if (card.dataset.sizes) {
          card.dataset.sizes.split(',').map(s => s.trim()).filter(Boolean).forEach(s => opts.sizes.add(s));
        }
        const p = parseInt(card.dataset.price, 10);
        if (!isNaN(p)) opts.prices.push(p);
      });
      return opts;
    }

    /* ── Price buckets ───────────────────────────────────────────────── */

    _computePriceBuckets(prices) {
      if (!prices.length) return [];
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      if (min === max) {
        return [{ key: `eq${min}`, label: this._formatPrice(min), min, max: Infinity }];
      }
      const step = Math.max(Math.round((max - min) / 3 / 500) * 500, 500);
      const t1   = min + step;
      const t2   = min + step * 2;
      const raw  = [
        { key: `lt${t1}`,       label: `< ${this._formatPrice(t1)}`,                              min: 0,      max: t1 - 1  },
        { key: `bt${t1}_${t2}`, label: `${this._formatPrice(t1)} – ${this._formatPrice(t2)}`,     min: t1,     max: t2      },
        { key: `gt${t2}`,       label: `> ${this._formatPrice(t2)}`,                              min: t2 + 1, max: Infinity }
      ];
      return raw.filter(b => prices.some(p => p >= b.min && (b.max === Infinity || p <= b.max)));
    }

    _formatPrice(cents) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(cents / 100);
    }

    /* ── Dropdowns ───────────────────────────────────────────────────── */

    _buildDropdowns() {
      this._dropItems = [];
      const opts      = this._scanOptions();
      const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

      this.querySelectorAll('[data-facet-toggle]').forEach(btn => {
        const key        = btn.dataset.filterKey;
        const dropdownId = btn.getAttribute('aria-controls');
        if (!key || !dropdownId) return;
        const dropdown = document.getElementById(dropdownId);
        if (!dropdown) return;

        this._dropItems.push({ btn, dropdown, key });

        /* Build items list per filter type */
        let items = [];
        if (key === 'color') {
          items = [...opts.color].sort().map(v => ({ value: v, label: v }));
        } else if (key === 'available') {
          const map = { true: 'En stock', false: 'Épuisé' };
          items = ['true', 'false']
            .filter(v => opts.available.has(v))
            .map(v => ({ value: v, label: map[v] }));
        } else if (key === 'sizes') {
          items = [...opts.sizes]
            .sort((a, b) => {
              const ai = sizeOrder.indexOf(a.toUpperCase());
              const bi = sizeOrder.indexOf(b.toUpperCase());
              if (ai !== -1 && bi !== -1) return ai - bi;
              return ai !== -1 ? -1 : bi !== -1 ? 1 : a.localeCompare(b, 'fr');
            })
            .map(v => ({ value: v, label: v }));
        }

        const wrapper = btn.closest('.facets-toolbar__btn-wrapper');
        if (wrapper) wrapper.hidden = !items.length;
        if (items.length) this._populateDropdown(dropdown, key, items);

        if (!btn._toolbarBound) {
          btn.addEventListener('click', () => {
            btn.getAttribute('aria-expanded') === 'true'
              ? this._closeAll()
              : this._openDropdown(key);
          });
          btn._toolbarBound = true;
        }
      });

      this._buildPriceSortToggle(opts);
    }

    _buildPriceSortToggle(opts) {
      if (!this._priceSortToggle || !this._priceSortOptions) return;

      const ul = this._priceSortOptions.querySelector('ul');
      if (!ul) return;

      const buckets = this._computePriceBuckets(opts.prices);
      buckets.forEach(b => { this._priceBuckets[b.key] = { min: b.min, max: b.max }; });

      const bar = this._priceSortToggle.closest('.collection__sort-bar--price');
      if (bar) bar.hidden = !buckets.length;

      ul.replaceChildren();
      const allUid = `price-sort-all-${this._sectionId()}`;
      const allLi  = document.createElement('li');
      allLi.className = 'collection__filter-tag';

      const allRadio              = document.createElement('input');
      allRadio.className          = 'collection__sort__input';
      allRadio.type               = 'radio';
      allRadio.name               = `price_sort_${this._sectionId()}`;
      allRadio.value              = '';
      allRadio.id                 = allUid;
      allRadio.checked            = (this._activeFilters['price']?.size ?? 0) === 0;
      allRadio.dataset.displayLabel = 'Tous les prix';

      const allLabel       = document.createElement('label');
      allLabel.className   = 'collection__sort__button';
      allLabel.htmlFor     = allUid;
      allLabel.setAttribute('tabindex', '0');
      allLabel.textContent = 'Tous les prix';

      allLi.appendChild(allRadio);
      allLi.appendChild(allLabel);
      ul.appendChild(allLi);

      allRadio.addEventListener('change', () => {
        if (!allRadio.checked) return;
        this._activeFilters['price']?.clear();
        this._applyFilters();
        this._updatePriceBtnLabel();
        this._syncClearBtn();
        this._priceSortClose();
      });

      buckets.forEach((b, i) => {
        const uid = `price-sort-${i}-${this._sectionId()}`;
        const li  = document.createElement('li');
        li.className = 'collection__filter-tag';

        const radio              = document.createElement('input');
        radio.className          = 'collection__sort__input';
        radio.type               = 'radio';
        radio.name               = `price_sort_${this._sectionId()}`;
        radio.value              = b.key;
        radio.id                 = uid;
        radio.checked            = this._activeFilters['price']?.has(b.key) ?? false;
        radio.dataset.displayLabel = b.label;

        const lbl       = document.createElement('label');
        lbl.className   = 'collection__sort__button';
        lbl.htmlFor     = uid;
        lbl.setAttribute('tabindex', '0');
        lbl.textContent = b.label;

        li.appendChild(radio);
        li.appendChild(lbl);
        ul.appendChild(li);

        radio.addEventListener('change', () => {
          const set = this._activeFilters['price'];
          set.clear();
          if (radio.checked) set.add(b.key);
          this._applyFilters();
          this._updatePriceBtnLabel();
          this._syncClearBtn();
          this._priceSortClose();
        });
      });
    }

    _populateDropdown(dropdown, key, items) {
      dropdown.replaceChildren();

      const ul = document.createElement('ul');
      ul.className = 'facets-toolbar__dropdown-list';

      items.forEach((item, i) => {
        const uid = `toolbar-${key}-${i}-${this._sectionId()}`;
        const li  = document.createElement('li');

        const cb          = document.createElement('input');
        cb.type           = 'checkbox';
        cb.id             = uid;
        cb.value          = item.value;
        cb.dataset.displayLabel = item.label;
        cb.checked        = this._activeFilters[key]?.has(item.value) ?? false;

        const lbl      = document.createElement('label');
        lbl.htmlFor    = uid;
        lbl.className  = 'facets-toolbar__dropdown-label';
        lbl.prepend(cb);
        lbl.append(document.createTextNode(item.label));
        li.appendChild(lbl);
        ul.appendChild(li);

        cb.addEventListener('change', () => {
          const set = this._activeFilters[key];
          if (set) cb.checked ? set.add(item.value) : set.delete(item.value);
          this._applyFilters();
          const dropItem = this._dropItems.find(d => d.key === key);
          if (dropItem) this._updateBtnLabel(dropItem.btn, key);
          this._syncClearBtn();
        });
      });

      dropdown.appendChild(ul);
    }

    _openDropdown(key) {
      this._closeAll();
      this._sortClose();
      this._priceSortClose();
      const item = this._dropItems.find(d => d.key === key);
      if (!item) return;

      /* Position relative to <facets-toolbar> (position:relative) to avoid
         overflow-x:auto clipping from .facets-toolbar__filters-row */
      const btnRect  = item.btn.getBoundingClientRect();
      const selfRect = this.getBoundingClientRect();
      item.dropdown.style.top  = `${btnRect.bottom - selfRect.top + 2}px`;
      item.dropdown.style.left = `${Math.max(0, btnRect.left - selfRect.left)}px`;

      item.btn.setAttribute('aria-expanded', 'true');
      item.dropdown.classList.add('is-open');
      this._openKey = key;
    }

    _closeAll() {
      this._dropItems.forEach(({ btn, dropdown }) => {
        btn.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
      });
      this._openKey = null;
    }

    _sortDropdownToggle() {
      if (!this._sortOptions) return;
      const visible = this._sortOptions.classList.contains('collection__sort__option-wrapper--visible');
      if (visible) {
        this._sortClose();
        return;
      }
      this._closeAll();
      this._priceSortClose();
      this._sortOptions.classList.add('collection__sort__option-wrapper--visible');
      this._sortToggle?.setAttribute('aria-expanded', 'true');
    }

    _sortClose() {
      if (!this._sortOptions) return;
      this._sortOptions.classList.remove('collection__sort__option-wrapper--visible');
      this._sortToggle?.setAttribute('aria-expanded', 'false');
    }

    _priceSortDropdownToggle() {
      if (!this._priceSortOptions) return;
      this._priceSortOptions.classList.contains('collection__sort__option-wrapper--visible')
        ? this._priceSortClose()
        : this._priceSortOpen();
    }

    _priceSortOpen() {
      this._closeAll();
      this._sortClose();
      const btnRect  = this._priceSortToggle.getBoundingClientRect();
      const selfRect = this.getBoundingClientRect();
      this._priceSortOptions.style.top  = `${btnRect.bottom - selfRect.top + 2}px`;
      this._priceSortOptions.style.left = `${Math.max(0, btnRect.left - selfRect.left)}px`;
      this._priceSortOptions.classList.add('collection__sort__option-wrapper--visible');
      this._priceSortToggle.setAttribute('aria-expanded', 'true');
    }

    _priceSortClose() {
      if (!this._priceSortOptions) return;
      this._priceSortOptions.classList.remove('collection__sort__option-wrapper--visible');
      this._priceSortToggle?.setAttribute('aria-expanded', 'false');
    }

    _onSortChange(e) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      window.location.assign(url.toString());
    }

    _onBodyClick(e) {
      if (!this.contains(e.target)) {
        this._priceSortClose();
        this._sortClose();
        this._closeAll();
      }
    }

    _onKeyUp(e) {
      if (e.key !== 'Escape') return;
      if (this._priceSortOptions?.classList.contains('collection__sort__option-wrapper--visible')) {
        this._priceSortClose();
        this._priceSortToggle?.focus();
        return;
      }
      if (this._sortOptions?.classList.contains('collection__sort__option-wrapper--visible')) {
        this._sortClose();
        this._sortToggle?.focus();
        return;
      }
      if (!this._openKey) return;
      const item = this._dropItems.find(d => d.key === this._openKey);
      this._closeAll();
      item?.btn.focus();
    }

    /* ── Filtering ───────────────────────────────────────────────────── */

    _applyFilters() {
      const cards   = this._allCards();
      let   visible = 0;

      cards.forEach(card => {
        const show = this._filterCard(card);
        card.hidden = !show;
        if (show) visible++;
      });

      this._updateCount(visible, cards.length);
      this._serializeFiltersToUrl();
    }

    _filterCard(card) {
      const { color, available, sizes, price } = this._activeFilters;

      if (color.size > 0) {
        const label = card.dataset.colorLabel ?? '';
        const alt   = card.dataset.imageAlt   ?? '';
        const matches = [...color].some(v =>
          label === v ||
          this._handleize(label) === this._handleize(v) ||
          alt.includes(this._handleize(v))
        );
        if (!matches) return false;
      }

      if (available.size > 0) {
        if (!available.has(card.dataset.available)) return false;
      }

      if (sizes.size > 0) {
        const cardSizes = (card.dataset.sizes ?? '').split(',').map(s => s.trim()).filter(Boolean);
        if (![...sizes].some(s => cardSizes.includes(s))) return false;
      }

      if (price.size > 0) {
        const cardPrice = parseInt(card.dataset.price, 10);
        if (isNaN(cardPrice)) return false;
        const matches = [...price].some(bk => {
          const b = this._priceBuckets[bk];
          return b && cardPrice >= b.min && (b.max === Infinity || cardPrice <= b.max);
        });
        if (!matches) return false;
      }

      return true;
    }

    /* ── UI ──────────────────────────────────────────────────────────── */

    _updateBtnLabel(btn, key) {
      if (!btn) return;
      const labelEl = btn.querySelector('.facets-toolbar__btn-label');
      const valueEl = btn.querySelector('.facets-toolbar__btn-value');
      if (!labelEl || !valueEl) return;

      const set = this._activeFilters[key];
      if (!set || set.size === 0) {
        valueEl.textContent = '';
        valueEl.hidden = true;
        btn.classList.remove('has-active');
        return;
      }

      btn.classList.add('has-active');
      valueEl.hidden = false;

      const item = this._dropItems.find(d => d.key === key);
      const displays = [...set].map(val => {
        const cb = item?.dropdown.querySelector(`input[value="${CSS.escape(val)}"]`);
        return cb?.dataset.displayLabel ?? val;
      });
      valueEl.textContent = displays.join(', ');
    }

    _updatePriceBtnLabel() {
      const btn = this._priceSortToggle;
      if (!btn) return;
      const labelEl = btn.querySelector('.facets-toolbar__btn-label');
      const valueEl = btn.querySelector('.facets-toolbar__btn-value');
      if (!labelEl || !valueEl) return;

      const set = this._activeFilters['price'];
      if (!set || set.size === 0) {
        valueEl.textContent = '';
        valueEl.hidden = true;
        btn.classList.remove('has-active');
        return;
      }
      btn.classList.add('has-active');
      valueEl.hidden = false;
      const displays = [...set].map(val => {
        const r = this._priceSortOptions?.querySelector(`input[value="${CSS.escape(val)}"]`);
        return r?.dataset.displayLabel ?? val;
      });
      valueEl.textContent = displays.join(', ');
    }

    _syncClearBtn() {
      const btn      = this.querySelector('[data-facets-clear-all]');
      if (!btn) return;
      const hasActive = Object.values(this._activeFilters).some(s => s.size > 0);
      btn.hidden = !hasActive;
    }

    _updateCount(visible, total) {
      const el = this._container.querySelector('[data-products-count]');
      if (!el) return;
      if (visible === undefined) {
        const cards = this._allCards();
        total   = cards.length;
        visible = cards.filter(c => !c.hidden).length;
      }
      el.textContent = visible >= total
        ? `${total} ${total === 1 ? 'produit' : 'produits'}`
        : `${visible} / ${total} produits`;
    }

    /* ── URL persistence ─────────────────────────────────────────────── */

    _initFiltersFromUrl() {
      const url = new URL(window.location.href);
      ['color', 'available', 'sizes', 'price'].forEach(key => {
        const val = url.searchParams.get(`filter_${key}`);
        if (val) val.split(',').filter(Boolean).forEach(v => this._activeFilters[key].add(v));
      });
    }

    _serializeFiltersToUrl() {
      const url = new URL(window.location.href);
      ['color', 'available', 'sizes', 'price'].forEach(key => {
        const set = this._activeFilters[key];
        const param = `filter_${key}`;
        if (set && set.size > 0) {
          url.searchParams.set(param, [...set].join(','));
        } else {
          url.searchParams.delete(param);
        }
      });
      history.replaceState(null, '', url.toString());
    }

    /* ── Helpers ─────────────────────────────────────────────────────── */

    _sectionId() {
      return this._container?.dataset.sectionId ?? '';
    }

    _handleize(str) {
      return (str ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036F]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }

  });

  /* ── "Tout effacer" global handler ──────────────────────────────────── */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-facets-clear-all]');
    if (!btn) return;
    e.preventDefault();
    const toolbar = btn.closest('facets-toolbar');
    if (!toolbar || !toolbar._activeFilters) return;

    Object.values(toolbar._activeFilters).forEach(s => s.clear());
    toolbar.querySelectorAll('.facets-toolbar__dropdown input[type="checkbox"]')
      .forEach(cb => { cb.checked = false; });
    const priceOptions = toolbar.querySelector('[data-price-sort-options]');
    if (priceOptions) {
      priceOptions.querySelectorAll('input[type="radio"]').forEach(r => { r.checked = r.value === ''; });
      toolbar._priceSortClose();
    }
    toolbar._dropItems.forEach(({ btn: b, key }) => toolbar._updateBtnLabel(b, key));
    toolbar._updatePriceBtnLabel();
    toolbar._applyFilters();
    toolbar._syncClearBtn();
  });

})();
